import {
    AnyRoute,
    ExtractRouteParams,
    MatchResult,
    Route,
    RouteOptions,
    RouteParams,
    RouteState,
    UrlBuilder,
} from '../types/routes.js'

/** Base path prefix for routing. Set via `setBasePath()` before app initialization. */
let basePath = ''

/** Configure the base path prefix for all routes. Call before initializing the app. */
export const setBasePath = (path: string) => {
    basePath = path
}

/** Get the current base path prefix. */
export const getBasePath = (): string => basePath

export const simpleRoute = <View>(
    path: string,
    options: RouteOptions<View, undefined>,
): Route<View, undefined> => ({
    match: (raw) => (raw === path ? { matches: true, params: undefined } : { matches: false }),
    toUrl: () => basePath + (path.startsWith('/') ? path : '/' + path),
    debug: path,
    ...options,
})

export const prefixRoute = <View>(
    prefix: string,
    options: RouteOptions<View, undefined>,
): Route<View, undefined> => ({
    match: (raw) =>
        raw.startsWith(prefix) ? { matches: true, params: undefined } : { matches: false },
    toUrl: () => basePath + (prefix.startsWith('/') ? prefix : '/' + prefix),
    debug: prefix + '*',
    ...options,
})

export const patternRoute = <
    View,
    Pattern extends string,
    Param extends ExtractRouteParams<Pattern> = ExtractRouteParams<Pattern>,
>(
    pattern: Pattern,
    options: RouteOptions<View, Param>,
): Route<View, Param> => {
    const patternSegments = pattern.split('/')
    const entries = Array.from(patternSegments.entries())

    // Example:
    // Pattern: '/foo/:fooId/bar/:barId'
    // Param: 'fooId' | 'barId'
    // patternSegments: ['foo', ':fooId', 'bar', ':barId']
    // literalIndexes: [0, 2] // 'foo', 'bar'
    // paramIndexes: [['fooId', 1], ['barId', 3]]

    const literalIndexes = entries
        .filter(([_, segment]) => !segment.startsWith(':'))
        .map(([index]) => index)

    const paramIndexes = entries
        .filter(([_, segment]) => segment.startsWith(':'))
        .map(([index, segment]) => [segment.slice(1), index] as [Param, number])

    const hashBuilder = {
        toUrl: (params) => {
            // Create a copy
            const segments = [...patternSegments]

            // Replace param name segments with values
            for (const [param, index] of paramIndexes) {
                segments[index] = String(params[param])
            }

            const path = segments.join('/')
            return basePath + (path.startsWith('/') ? path : '/' + path)
        },
    } as UrlBuilder<Param>

    return {
        ...hashBuilder,
        match: (raw): MatchResult<Param> => {
            const segments = raw.split('/')

            // Example:
            // raw: '/foo/123/bar/9fb4-e810'
            // segments: ['foo', '123', 'bar', '9fb4-e810']
            // params: { fooId: '123', barId: '9fb4-e810' }

            if (segments.length !== patternSegments.length) {
                return { matches: false }
            }

            if (!literalIndexes.every((index) => segments[index] === patternSegments[index])) {
                return { matches: false }
            }

            const params = Object.fromEntries(
                paramIndexes.map(([param, index]) => [param, segments[index]]),
            ) as RouteParams<Param, string>

            return { matches: true, params }
        },
        debug: pattern,
        ...options,
    }
}

export const catchAllRoute = <View>(
    options: RouteOptions<View, undefined>,
): Route<View, undefined> => ({
    match: () => ({ matches: true, params: undefined }),
    toUrl: () => basePath + '/',
    debug: '*',
    ...options,
})

export const getRawRoute = (): string => {
    const pathname = window.location.pathname
    if (basePath && pathname.startsWith(basePath)) {
        const rest = pathname.slice(basePath.length)
        // Strip leading / so routes without leading / (admin routes) can match
        return rest.startsWith('/') ? rest.slice(1) : rest
    }
    return pathname
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRouteState = <View>(routes: AnyRoute<View>[]): RouteState<View, any> => {
    const raw = getRawRoute()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const route of routes as Route<View, any>[]) {
        const match = route.match(raw)

        if (match.matches) {
            return { raw, definition: route, params: match.params }
        }
    }

    throw new Error(`No route found for ${raw}`)
}

export const navigateTo = (url: string) => {
    history.pushState({}, '', url)
    window.dispatchEvent(new PopStateEvent('popstate'))
}

export interface TabSource {
    // Query mode: read the active key from this query param (defaults to 'tab').
    param?: string
    // Path mode: map of tab key -> the route path that "owns" it. The longest
    // matching prefix wins (so '/me/tasks' beats '/me').
    paths?: Record<string, string>
    // Returned when nothing matches.
    fallback: string
    // Legacy query-value aliases, e.g. { integrations: 'settings' }.
    aliases?: Record<string, string>
}

// Derives the active tab/section key from the current URL — the single source
// of truth for tab active-state across the app, in either path or query mode.
export const activeTabFromUrl = (source: TabSource): string => {
    if (source.paths) {
        const { pathname } = window.location
        const match = Object.entries(source.paths)
            .filter(([, path]) => pathname === path || pathname.startsWith(path + '/'))
            .sort((a, b) => b[1].length - a[1].length)[0]
        return match ? match[0] : source.fallback
    }
    const raw = new URLSearchParams(window.location.search).get(source.param ?? 'tab')
    if (!raw) return source.fallback
    return source.aliases?.[raw] ?? raw
}
